import React from 'react';
import { Link } from 'react-router-dom';
import { Check, ArrowRight, ChevronRight } from 'lucide-react';
import { Seo } from '../components/Seo';
import { SITE, LANDING_PAGES, faqJsonLd, breadcrumbJsonLd, localBusinessJsonLd } from '../data/seoData';

export default function SeoLandingPage({ slug }) {
  const page = LANDING_PAGES.find((p) => p.slug === slug);
  if (!page) return null;

  const jsonLd = [
    faqJsonLd(page.faqs),
    breadcrumbJsonLd([
      { name: 'Home', path: '/' },
      { name: page.h1, path: `/${page.slug}` },
    ]),
    ...(page.includeLocalBusiness ? [localBusinessJsonLd()] : []),
  ];

  return (
    <div className="min-h-screen bg-white" data-testid={`landing-${page.slug}`}>
      <Seo
        title={page.metaTitle}
        description={page.metaDescription}
        path={`/${page.slug}`}
        jsonLd={jsonLd}
      />

      {/* HERO */}
      <section className="bg-[#F4F7F2]">
        <div className="max-w-4xl mx-auto px-6 py-14 md:py-20">
          <nav className="flex items-center gap-1.5 text-sm text-[#1F3D2C]/60 mb-5" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-[#4A7C59]">Home</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <span className="text-[#14281D] font-medium">{page.h1}</span>
          </nav>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight leading-[1.08] text-[#14281D]">
            {page.h1}
          </h1>
          <p className="mt-5 text-base md:text-lg text-[#1F3D2C]/80 leading-relaxed">{page.intro}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/quote" data-testid={`landing-${page.slug}-quote-cta`}>
              <button className="bg-[#6FBF73] hover:bg-[#4A7C59] text-white px-7 py-3.5 rounded-full text-base font-semibold shadow-md transition-colors inline-flex items-center gap-2">
                Get an Instant Quote <ArrowRight className="w-4 h-4" />
              </button>
            </Link>
            <Link to={page.categoryPath} data-testid={`landing-${page.slug}-category-cta`}>
              <button className="bg-white border border-[#6FBF73] text-[#4A7C59] hover:bg-[#6FBF73] hover:text-white px-7 py-3.5 rounded-full text-base font-semibold transition-colors">
                {page.categoryLabel}
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* BULLETS */}
      <section className="max-w-4xl mx-auto px-6 py-12">
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {page.bullets.map((b) => (
            <li key={b} className="flex items-start gap-3 bg-[#F4F7F2] rounded-xl px-4 py-3">
              <Check className="w-5 h-5 text-[#6FBF73] mt-0.5 shrink-0" />
              <span className="text-[#1F3D2C] text-sm md:text-base">{b}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* COMPARISON TABLE (optional) */}
      {page.table && (
        <section className="max-w-4xl mx-auto px-6 pb-8">
          <div className="overflow-x-auto rounded-xl border border-[#E5E7EB]" data-testid="landing-comparison-table">
            <table className="w-full text-sm md:text-base">
              <caption className="sr-only">{page.table.caption}</caption>
              <thead>
                <tr className="bg-[#14281D] text-white">
                  {page.table.headers.map((h, i) => (
                    <th key={i} className="px-4 py-3 text-left font-semibold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {page.table.rows.map((row, ri) => (
                  <tr key={ri} className={ri % 2 ? 'bg-[#F4F7F2]' : 'bg-white'}>
                    {row.map((cell, ci) => (
                      <td key={ci} className={`px-4 py-3 text-[#1F3D2C] ${ci === 0 ? 'font-semibold text-[#14281D]' : ''}`}>
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* CONTENT SECTIONS */}
      <section className="max-w-4xl mx-auto px-6 pb-4 space-y-10">
        {page.sections.map((s) => (
          <div key={s.heading}>
            <h2 className="text-base md:text-lg font-bold text-[#14281D] mb-3">{s.heading}</h2>
            <p className="text-[#1F3D2C]/80 leading-relaxed text-sm md:text-base">{s.body}</p>
          </div>
        ))}
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-6 py-12">
        <h2 className="text-base md:text-lg font-bold text-[#14281D] mb-6">Frequently Asked Questions</h2>
        <div className="space-y-4">
          {page.faqs.map((f) => (
            <details key={f.q} className="group bg-[#F4F7F2] rounded-xl px-5 py-4" data-testid={`landing-faq-item`}>
              <summary className="cursor-pointer font-semibold text-[#14281D] text-sm md:text-base list-none flex items-center justify-between">
                {f.q}
                <ChevronRight className="w-4 h-4 text-[#6FBF73] transition-transform group-open:rotate-90" />
              </summary>
              <p className="mt-3 text-[#1F3D2C]/80 text-sm md:text-base leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-[#F4F7F2] py-14">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-base md:text-lg font-bold text-[#14281D] mb-2">Ready to see your price?</h2>
          <p className="text-[#1F3D2C]/70 mb-6 text-sm md:text-base">
            Instant online pricing — any size, shape and quantity. No emails, no waiting.
          </p>
          <Link to="/quote" data-testid={`landing-${page.slug}-final-cta`}>
            <button className="bg-[#6FBF73] hover:bg-[#4A7C59] text-white px-8 py-3.5 rounded-full text-base font-semibold shadow-md transition-colors">
              Start Your Quote
            </button>
          </Link>
        </div>
      </section>

      {/* FOOTER NAP */}
      <footer className="bg-[#14281D] text-white py-10">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-emerald-100">© 2026 {SITE.name}. All rights reserved.</p>
          <p className="text-sm text-emerald-200/80 mt-2">
            {SITE.address.street}, {SITE.address.city}, {SITE.address.region} {SITE.address.postal}
          </p>
          <p className="text-sm text-emerald-200/80 mt-1">Phone: {SITE.phone} | Email: {SITE.email}</p>
        </div>
      </footer>
    </div>
  );
}
